import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, AlertCircle, Loader2 } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";
import CityPickerInput from "@/components/price/CityPickerInput";

/**
 * Price — Popup admin pour les utilisateurs (client / covoitureur).
 *
 * Flow :
 *   1. Poll toutes les 60 s la liste des popups pending côté user.
 *   2. Si au moins une popup non dismiss → affiche une modale bloquante
 *      centrée. L'utilisateur doit :
 *        - cliquer sur l'action ("Configurer maintenant" par ex.) →
 *          `act` côté serveur, la popup disparaît définitivement ;
 *        - OU cliquer sur "Fermer" / X → `dismiss` côté serveur ; la
 *          modale se ferme mais un bandeau persistant reste tant que
 *          l'user n'a pas agi.
 *   3. Une popup sans action_label passe direct en "act" quand l'user
 *      clique OK.
 *
 * Props :
 *   - onActionTarget(target: string) : callback quand l'user clique
 *     l'action. Le parent (dashboard) redirige vers le sheet approprié.
 */
// Partagé entre remounts (changement de vue) pour ne pas ré-ouvrir une popup déjà vue.
const SEEN_MODAL_IDS = new Set();

export default function AdminPopupCenter({ onActionTarget }) {
  const [items, setItems] = useState([]);
  const [modalItem, setModalItem] = useState(null); // popup active affichée en modale
  const [busy, setBusy] = useState(false);
  const [cityPick, setCityPick] = useState(null); // pour les popups set_city
  const seenModalIds = useRef(SEEN_MODAL_IDS);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/price/popups/pending");
      const list = Array.isArray(r.data?.items) ? r.data.items : [];
      setItems(list);
      // Si une popup jamais dismiss + jamais vue en modale dans cette
      // session → on l'ouvre en modale bloquante.
      const firstNew = list.find(
        (p) => !p.dismissed_at && !seenModalIds.current.has(p.id),
      );
      if (firstNew) {
        seenModalIds.current.add(firstNew.id);
        setModalItem(firstNew);
      }
    } catch (_) { /* silencieux */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  const dismiss = async (item) => {
    if (!item || busy) return;
    setBusy(true);
    try {
      await api.post(`/price/popups/${item.id}/dismiss`);
      setModalItem(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const act = async (item) => {
    if (!item || busy) return;
    // Cas spécial : popup "set_city" → l'action = sauver la ville
    // choisie dans le CityPicker inline AVANT de marquer le popup
    // comme acté. Si pas de ville sélectionnée, on bloque.
    if (item.action_target === "set_city") {
      if (!cityPick?.lat) {
        toast.error("Choisis ta ville dans la liste");
        return;
      }
      setBusy(true);
      try {
        await api.post("/price/profile/city", {
          city: cityPick.name,
          lat: cityPick.lat,
          lng: cityPick.lng,
        });
        await api.post(`/price/popups/${item.id}/act`);
        toast.success("Ville enregistrée — merci !");
        setModalItem(null);
        setCityPick(null);
        setItems((prev) => prev.filter((p) => p.id !== item.id));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erreur d'enregistrement");
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      await api.post(`/price/popups/${item.id}/act`);
      setModalItem(null);
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      if (item.action_target === "store_update") {
        // Détection plateforme robuste : Capacitor native > UA fallback.
        const ua = (navigator.userAgent || "").toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
        const isAndroid = /android/.test(ua);
        // URL App Store — doit rester synchronisée avec PriceDownload.jsx.
        const iosUrl = "https://apps.apple.com/fr/app/price-covoit/id6793724470";
        const androidUrl = "https://play.google.com/store/apps/details?id=com.price.covoiturage";
        const url = isIOS ? iosUrl : (isAndroid ? androidUrl : "https://pricecovoit.fr/telecharger");
        window.open(url, "_blank", "noopener,noreferrer");
      } else if (item.action_target === "custom_link" && item.link_url) {
        // Notif personnalisée avec URL externe. Ouvre dans un nouvel
        // onglet pour ne pas casser l'app en cours.
        window.open(item.link_url, "_blank", "noopener,noreferrer");
      } else if (item.action_target && item.action_target !== "none") {
        onActionTarget?.(item.action_target);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // Bandeau persistant : première popup déjà dismiss mais pas encore
  // acted, avec une action pending.
  const bannerItem = items.find(
    (p) => p.dismissed_at && p.action_label && p.action_target && p.action_target !== "none",
  );

  return (
    <>
      {/* Modale bloquante */}
      {modalItem && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          data-testid="admin-popup-modal"
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-none grid place-items-center rounded-2xl bg-[#FFE5E5] border-2 border-black shadow-[0_3px_0_0_#000]">
                  <AlertCircle className="w-5 h-5 text-[#FF3B3B]" strokeWidth={2.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-[Outfit] font-black uppercase tracking-widest text-neutral-500">
                    Message de l&apos;équipe Price
                  </div>
                  <h3
                    className="mt-1 font-[Outfit] text-[19px] font-black leading-tight text-black"
                    data-testid="admin-popup-title"
                  >
                    {modalItem.title}
                  </h3>
                </div>
              </div>
              <p
                className="mt-4 font-[Outfit] text-[14px] leading-[1.5] text-neutral-800 whitespace-pre-wrap"
                data-testid="admin-popup-body"
              >
                {modalItem.body}
              </p>

              {/* Image AU CENTRE (sous le message) — plus impactante que
                  collée en top, s'intègre au flow de lecture naturel. */}
              {modalItem.image_url && (
                <div className="mt-4 rounded-2xl border-2 border-black overflow-hidden bg-neutral-50">
                  <img
                    src={resolveMediaUrl(modalItem.image_url)}
                    alt=""
                    className="w-full h-auto max-h-[340px] object-cover"
                    data-testid="admin-popup-image"
                  />
                </div>
              )}

              {/* Lien externe JUSTE EN DESSOUS de l'image, cliquable direct
                  (en plus du bouton d'action dans le footer, pour ceux qui
                  scannent le popup rapidement). */}
              {modalItem.action_target === "custom_link" && modalItem.link_url && (
                <a
                  href={modalItem.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-popup-link"
                  className="mt-3 flex items-center justify-center gap-1.5 w-full rounded-2xl bg-[#0F2634] text-white font-[Outfit] font-black text-[13px] px-4 py-3 border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all"
                >
                  <span className="truncate">🔗 {modalItem.link_url.replace(/^https?:\/\//, "")}</span>
                </a>
              )}

              {/* Popup "set_city" — CityPicker intégré directement dans
                  la modale. L'utilisateur choisit sa ville et clique sur
                  le bouton d'action pour l'enregistrer. */}
              {modalItem.action_target === "set_city" && (
                <div className="mt-4" data-testid="admin-popup-city-picker">
                  <CityPickerInput
                    value={cityPick}
                    onChange={setCityPick}
                    testid="admin-popup-city"
                    label="Ta ville"
                    required
                  />
                </div>
              )}
            </div>
            <div className="border-t-2 border-black bg-[#FFF8F1] px-4 py-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => dismiss(modalItem)}
                disabled={busy}
                data-testid="admin-popup-dismiss"
                className="flex-1 h-12 rounded-2xl bg-white text-black font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-50"
              >
                Plus tard
              </button>
              {modalItem.action_label && modalItem.action_target !== "none" ? (
                <button
                  type="button"
                  onClick={() => act(modalItem)}
                  disabled={busy}
                  data-testid="admin-popup-act"
                  className="flex-[1.4] h-12 rounded-2xl bg-[#FF3B3B] text-white font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modalItem.action_label}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => act(modalItem)}
                  disabled={busy}
                  data-testid="admin-popup-ok"
                  className="flex-1 h-12 rounded-2xl bg-[#FF3B3B] text-white font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60"
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bandeau persistant : rappel si l'user a fermé sans agir */}
      {!modalItem && bannerItem && (
        <button
          type="button"
          onClick={() => setModalItem(bannerItem)}
          data-testid="admin-popup-banner"
          className="w-full mt-3 flex items-center gap-3 rounded-2xl bg-[#FFF3CC] p-3 border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all text-left"
        >
          <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-white border-2 border-black">
            <AlertCircle className="h-5 w-5 text-[#B58900]" strokeWidth={2.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-[Outfit] text-[13px] font-black leading-tight text-black truncate">
              {bannerItem.title}
            </div>
            <div className="mt-0.5 text-[11px] font-medium text-neutral-700 truncate">
              Appuie pour {bannerItem.action_label?.toLowerCase() || "voir"}
            </div>
          </div>
          <X className="w-4 h-4 text-neutral-500" strokeWidth={2.5} />
        </button>
      )}
    </>
  );
}
